import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";

const getCustomerLabel = (customer, displayMode) => {
  if (!customer) {
    return "";
  }

  return displayMode === "number" ? customer.customerNumber : customer.label;
};

const CustomerSearchSelect = ({
  customers,
  value,
  onSelect,
  onClear,
  label = "Search customer number or name",
  helperText = " ",
  error = false,
  disabled = false,
  size,
  sx,
  displayMode = "label",
  clearOnType = false,
  invoiceFieldKey,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const optionRefs = useRef([]);

  const customerOptions = useMemo(() => {
    return customers
      .filter((customer) => Number(customer.typeRelation) === 1)
      .map((customer) => {
        const customerNumber = String(customer.entityNumber || "").trim();
        const customerName = String(customer.name || "").trim();

        return {
          customerNumber,
          customerName,
          label: customerName ? `${customerNumber} - ${customerName}` : customerNumber,
        };
      })
      .filter((customer) => customer.customerNumber)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
  }, [customers]);

  const selectedCustomer = useMemo(
    () => customerOptions.find((customer) => customer.customerNumber === value),
    [customerOptions, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return [];
    }

    return customerOptions
      .filter((customer) => {
        return (
          customer.customerNumber.toLowerCase().includes(normalizedSearch) ||
          customer.customerName.toLowerCase().includes(normalizedSearch)
        );
      })
      .slice(0, 10);
  }, [customerOptions, searchValue]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue(getCustomerLabel(selectedCustomer, displayMode));
    }
  }, [displayMode, isOpen, selectedCustomer]);

  useEffect(() => {
    if (!isOpen || filteredOptions.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    setHighlightedIndex((prevIndex) => {
      if (prevIndex < 0) {
        return 0;
      }

      return Math.min(prevIndex, filteredOptions.length - 1);
    });
  }, [filteredOptions, isOpen]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) {
      return;
    }

    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [filteredOptions, highlightedIndex, isOpen]);

  const handleSelect = (customer) => {
    onSelect(customer);
    setSearchValue(getCustomerLabel(customer, displayMode));
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      const trimmedSearch = searchValue.trim();
      const exactMatch = customerOptions.find(
        (customer) =>
          customer.customerNumber.toLowerCase() === trimmedSearch.toLowerCase() ||
          customer.label.toLowerCase() === trimmedSearch.toLowerCase()
      );

      if (exactMatch) {
        handleSelect(exactMatch);
      } else if (!trimmedSearch) {
        onClear?.();
        setSearchValue("");
      } else {
        setSearchValue(getCustomerLabel(selectedCustomer, displayMode));
      }

      setIsOpen(false);
    }, 100);
  };

  const handleKeyDown = (event) => {
    if (!isOpen || filteredOptions.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prevIndex) => (prevIndex >= filteredOptions.length - 1 ? 0 : prevIndex + 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prevIndex) => (prevIndex <= 0 ? filteredOptions.length - 1 : prevIndex - 1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selectedOption = filteredOptions[highlightedIndex];
      if (selectedOption) {
        handleSelect(selectedOption);
      }
    }
  };

  return (
    <Box sx={{ position: "relative", ...sx }}>
      <TextField
        fullWidth
        size={size}
        label={label}
        inputProps={invoiceFieldKey ? { "data-invoice-field": invoiceFieldKey } : undefined}
        value={searchValue}
        onFocus={() => {
          if (searchValue.trim()) {
            setIsOpen(true);
          }
        }}
        onChange={(event) => {
          const nextSearch = event.target.value;
          setSearchValue(nextSearch);
          if (clearOnType && value) {
            onClear?.();
          }
          setIsOpen(Boolean(nextSearch.trim()));
          setHighlightedIndex(0);
        }}
        onPaste={(event) => {
          event.preventDefault();
          const pasted = event.clipboardData.getData("text").trim();
          setSearchValue(pasted);
          if (clearOnType && value) {
            onClear?.();
          }
          setIsOpen(Boolean(pasted));
          setHighlightedIndex(0);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        error={error}
        helperText={helperText}
        disabled={disabled}
      />
      {isOpen && searchValue.trim() && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            maxHeight: 220,
            overflowY: "auto",
            backgroundColor: "background.paper",
            zIndex: 5,
            boxShadow: 3,
          }}
        >
          {filteredOptions.length === 0 ? (
            <Typography variant="body2" sx={{ p: 1.5, color: "text.secondary" }}>
              No customers found.
            </Typography>
          ) : (
            filteredOptions.map((customer, index) => (
              <Button
                key={customer.customerNumber}
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                fullWidth
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => handleSelect(customer)}
                sx={{
                  justifyContent: "flex-start",
                  borderRadius: 0,
                  textTransform: "none",
                  backgroundColor: highlightedIndex === index ? "action.selected" : "transparent",
                }}
              >
                {customer.label}
              </Button>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default CustomerSearchSelect;
